<template>
  <div class="flex flex-col gap-8">
    <div
      class="flex flex-col gap-8"
      :class="{
        'glasses-pdp-gallery relative -mx-4 -my-1 max-w-[100vw] bg-[#F2F3F5] p-4 md:-mx-3 md:-mt-4 md:flex md:flex-col md:justify-end lg:max-w-[913px] lg:pb-8':
          isEyewear,
      }"
    >
      <slot name="gallery-header" />
      <!-- looping should be enabled once SCFIM-2176 is resolved -->
      <ZoomGallery
        ref="zoomGalleryRef"
        v-model="activeImageIndex"
        :loop="isEyewear"
        :layout="isEyewear ? 'rx' : ''"
        :class="{
          'has-vto': showVto,
        }"
        :images="images"
        :product-name="productName"
      />
      <div class="btn-grid grid">
        <div
          class="m-auto flex flex-col gap-1 only:justify-center sm:col-start-2 sm:flex-row sm:justify-center md:gap-2"
        >
          <VTOFittingBox
            v-if="hasVto(product) && selectedVariant"
            class="max-w-2xs flex-1"
            :product="product"
            :variant="selectedVariant"
            data-allow-mismatch
            @on-vto-action="
              ({ product, variant }) =>
                product && variant && $emit('onVtoAction', { product, variant })
            "
            @on-vto-open="() => $emit('onVtoOpen')"
            @on-vto-close="() => $emit('onVtoClose')"
          />
          <DiscoverSimilar
            v-if="!isContactLensOrCareProduct"
            type="button"
            class="max-w-2xs flex-1"
            :placement="placement"
            :variant-or-product-id="selectedVariant?.id || product.id"
            :product-image="product.images[activeImageIndex]"
          />
        </div>
        <ZoomButton
          :icon-size="5"
          class="z-10 px-2 sm:col-start-3"
          @click="openZoomGallery"
        />
      </div>
    </div>
    <div v-if="showSiblings && hasSiblings" class="w-full">
      <Headline tag="span" look="h3">
        {{ $t('pdp.colors_heading') }}
      </Headline>
      <ProductDetailSiblingSlider
        class="mt-3 max-w-[100vw]"
        :product="product"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { getProductName } from '~/utils/product'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  selectedVariant: {
    type: Object as PropType<Variant | undefined>,
    default: undefined,
  },
  hasSiblings: {
    type: Boolean as PropType<boolean | undefined>,
    default: undefined,
  },
  isEyewear: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  showVto: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  showSiblings: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const zoomGalleryRef = ref()
const activeImageIndex = ref(0)

const placement = computed<SyteValidPlacement>(() => {
  if (props.selectedVariant?.stock.quantity === 0) {
    return 'PDP-oos'
  }
  return activeImageIndex.value === 0 ? 'PDP-main' : 'PDP-product-image'
})

const isContactLensOrCareProduct = computed(
  () => isContactLens(props.product) || isCareProducts(props.product),
)

defineEmits<{
  (e: 'onVtoAction', value: { product: Product; variant: Variant }): void
  (e: 'onVtoClose' | 'onVtoOpen'): void
}>()

const openZoomGallery = () => zoomGalleryRef.value.open()
const images = computed(() =>
  getProductImagesForGallery(props.product, props.selectedVariant),
)
const productName = getProductName(props.product)
</script>

<style lang="scss" scoped>
.btn-grid {
  grid-template-columns: 1fr 40px;

  @media (width >= 640px) {
    grid-template-columns: 40px 1fr 40px;
  }
}

@media (width >= 1024px) {
  .glasses-pdp-gallery {
    aspect-ratio: 1;
    max-width: calc(100% + 1.5rem); /* hack for safari */
  }
}
</style>
